import FuseSplashScreen from '@ameroservices-platform/attraction-backend/fuse/core/FuseSplashScreen';
import firebaseService from '@ameroservices-platform/shared/services/firebase';
import { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from '@reduxjs/toolkit';
import { hideMessage, showMessage } from '@ameroservices-platform/attraction-backend/app/store/fuse/messageSlice';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { logoutUser, setUserData, setUserDataFirebase } from './store/userSlice';
import organisationType from '@ameroservices-platform/shared/enums/organisationType';
import { getLaunchDarklyClientSideId } from '@ameroservices-platform/shared/ui-components/launchDarkly/clientSideId';

class Auth extends Component {
	state = {
		waitAuthCheck: true,
		ldProvider: null
	};

	componentDidMount() {
		return this.firebaseCheck()
			.then(user => {
				this.setState({ waitAuthCheck: false });
				return Promise.all([this.ldCheck(user), this.configCheck()]);
			})
			.then(([LDProvider]) => {
				this.setState({ ldProvider: LDProvider });
			});
	}

	configCheck = async () => {
		if (firebaseService.getOrganisationId()) {
			await firebaseService.requestConfig();
		}
	};

	ldCheck = async data => {
		const user = {};
		if (data) {
			user.key = data.uid;
		}
		if (!user.key) {
			delete user.key;
			user.anonymous = true;
		}
		if (data?.data) {
			if (data.data.displayName) {
				user.name = data.data.displayName;
			}
			if (data.data.email) {
				user.email = data.data.email;
			}
		}
		if (data?.claims) {
			user.custom = {
				organisationUid: data.claims?.organisationIds?.[organisationType.ATTRACTION] || undefined,
				role: data.claims.role || undefined,
				email_verified: data.claims.email_verified || undefined,
				mode: data.claims.mode || undefined
			};
		}
		const options = {};
		console.log('launchDarkly init with user and options: ', user, options, this.props);
		const LDProvider = await asyncWithLDProvider({
			clientSideID: getLaunchDarklyClientSideId(),
			user,
			options
		});
		if (process.env.REACT_APP_LD_CLIENTSIDEID) {
			console.log('USING ANOTHER CLIENT SIDE ID: ' + process.env.REACT_APP_LD_CLIENTSIDEID);
		}
		return LDProvider;
	};

	firebaseCheck = () =>
		new Promise(resolve => {
			firebaseService.init(
				success => {
					if (!success) {
						resolve(null);
					}
					firebaseService.onAuthStateChanged(authUser => {
						console.log('authUser', authUser);
						window.clarity('consent');
						if (authUser && authUser.emailVerified === false) {
							firebaseService.sendEmailVerification().then(() => {
								firebaseService.signOut();
								console.log('Email verification mail re-send');
							});

							return;
						}

						if (authUser && authUser.emailVerified === true) {
							this.props.showMessage({ message: 'Logger på...' });

							firebaseService.getUserData(authUser.uid).then(
								user => {
									const claimsEmail = user.claims ? user.claims.email : '';
									const claimsRole = user.claims ? user.claims.role : 'user';
									const claimsOrganisationId =
										user.claims &&
										user.claims.organisationIds &&
										firebaseService.getOrganisationType() &&
										user.claims.organisationIds[firebaseService.getOrganisationType()]
											? user.claims.organisationIds[firebaseService.getOrganisationType()]
											: null;
									// const claimsSignedSearchKey = user.claims ? user.claims.signedSearchKey : null;
									console.log('user', user);
									if (claimsEmail.endsWith('@amero.dk') && claimsRole !== 'ameroAdmin') {
										console.log(
											'Amero user without ameroAdmin role detected, requesting promotion'
										);
										firebaseService.requestAuthPromoteToAmeroAdmin().then(() => {
											console.log('Promoted, refreshing token');
											firebaseService.refreshToken().then(() => {
												console.log('Token refresh done, reloading app');
												window.location.reload();
											});
										});
										return;
									}
									if (
										claimsEmail.length > 0 &&
										!claimsOrganisationId &&
										claimsRole !== 'ameroAdmin'
									) {
										console.log(
											'Non ameroAdmin without organisationId, requesting change to default organisation'
										);
										const setAuthOrganisationCallable = firebaseService
											.getFunctions()
											.httpsCallable('authSetOrganisation');

										setAuthOrganisationCallable({})
											.then(resp => {
												console.log('Organisation set, refreshing token');
												firebaseService.refreshToken().then(() => {
													console.log('Token refresh done, reloading app');
													window.location.reload();
												});
											})
											.catch(err => {
												showMessage({
													message: `Fejl under tilknytning til organisation, kontakt Amero support`
												});
												console.error(
													'auth, no organisationId, not ameroAdmin, trying to set default, setAuthOrganisationCallable ERROR',
													err
												);
											});

										return;
									} else if (
										claimsEmail.length > 0 &&
										!user?.claims?.salesChannelIds &&
										claimsRole !== 'ameroAdmin'
									) {
										console.log(
											'Non ameroAdmin without salesChannelId, requesting change to default organisation'
										);
										const setAuthOrganisationCallable = firebaseService
											.getFunctions()
											.httpsCallable('authSetOrganisation');

										setAuthOrganisationCallable({})
											.then(resp => {
												console.log('Organisation set, refreshing token');
												firebaseService.refreshToken().then(() => {
													console.log('Token refresh done, reloading app');
													window.location.reload();
												});
											})
											.catch(err => {
												showMessage({
													message: `Fejl under tilknytning til organisation, kontakt Amero support`
												});
												console.error(
													'auth, no organisationId, not ameroAdmin, trying to set default, setAuthOrganisationCallable ERROR',
													err
												);
											});

										return;
									}
									this.props.setUserDataFirebase(user, authUser);
									resolve(user);
									this.props.hideMessage();
								},
								error => {
									resolve(null);
								}
							);
						} else {
							resolve(null);
						}
					});
				},
				null,
				organisationType.ATTRACTION
			);

			// if (!emailOfRequestingUser || ) {

			return Promise.resolve();
		});

	render() {
		if (this.state.waitAuthCheck) {
			return <FuseSplashScreen />;
		}
		if (this.state.ldProvider) {
			const LDComponent = this.state.ldProvider;
			return <LDComponent>{this.props.children}</LDComponent>;
		}

		return <FuseSplashScreen />;
	}
}

function mapStateToProps({ auth }) {
	return {
		user: auth.user
	};
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators(
		{
			logout: logoutUser,
			setUserData,
			setUserDataFirebase,
			showMessage,
			hideMessage
		},
		dispatch
	);
}

export default connect(mapStateToProps, mapDispatchToProps)(Auth);
